import {useCallback, useEffect, useRef, useState} from 'react'
import {toastMessage} from '../Toast'
import {debugLogger} from '../utils/debugLogger'

type MessageListener<M extends Record<T, any>, T extends string = any> = (message: M[T]) => void

const useWebsocket = <M extends Record<T, any>, T extends string = any>() => {
  const ws = useRef<WebSocket | null>(null)
  const reconnectIntervalRef = useRef(1000)
  const pingInterval = useRef<NodeJS.Timeout | null>(null)
  const messageListenersRef = useRef<MessageListener<M, T>[]>([])

  const [isOpen, setIsOpen] = useState(false)
  const [isConnectProcessing, setIsConnectProcessing] = useState(false)
  const hasError = useRef(false)

  const shouldReconnect = useRef(true)

  const disconnect = () => {
    if (ws.current) {
      shouldReconnect.current = false
      ws.current.close()
      ws.current = null
    }
  }

  useEffect(() => () => disconnect(), [])

  const connectWebSocket = (url: string) =>
    new Promise<WebSocket>((res, rej) => {
      setIsConnectProcessing(true)
      try {
        console.log('try')
        const socket = new WebSocket(url)
        ws.current = socket

        ws.current.onopen = () => {
          setIsOpen(true)
          debugLogger({
            message: 'Connected',
            tag: 'WebSocket',
          })
          reconnectIntervalRef.current = 1000 // Reset reconnection interval on successful connection

          pingInterval.current = setInterval(() => {
            if (ws.current?.readyState === WebSocket.OPEN) {
              ws.current.send(JSON.stringify({type: 'ping'}))
            }
          }, 30000)

          setIsConnectProcessing(false)
          res(socket)
        }

        ws.current.onmessage = event => {
          const message = JSON.parse(event.data || '{}')
          messageListenersRef.current.forEach(fun => fun(message))
        }

        ws.current.onerror = _error => {
          ws.current = null

          debugLogger({
            message: 'Could not connect',
            tag: 'WebSocket',
          })
          console.error('error', _error)
          toastMessage.error('Could not connect to websockets')

          hasError.current = true
          setIsOpen(false)
          setIsConnectProcessing(false)
          rej()
        }

        ws.current.onclose = _event => {
          if (_event.target !== ws.current) {
            return
          }

          setIsOpen(false)

          if (hasError.current) {
            // TODO: is there a better way to handle this?
            // Like detect why the error is caused?
            return
          }

          // Attempt to reconnect
          if (shouldReconnect.current) {
            setTimeout(() => {
              reconnectIntervalRef.current = Math.min(
                reconnectIntervalRef.current * 2,
                30000, // Exponential backoff, max 30 seconds
              )
              connectWebSocket(url)
            }, reconnectIntervalRef.current)
          } else {
            ws.current = null
          }
        }
      } catch (error) {
        ws.current = null

        debugLogger({
          message: 'Could not connect',
          tag: 'WebSocket',
        })
        toastMessage.error('Could not connect to websockets')
        hasError.current = true

        setIsOpen(false)
        setIsConnectProcessing(false)
        rej()
      }
    })

  const connect = async (url: string) => {
    if (ws.current) {
      return
    }

    shouldReconnect.current = true
    return connectWebSocket(url)
  }

  const sendMessage = useCallback((message: string) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(message)
    }
  }, [])

  const unsubscribeMessage = useCallback((listener: MessageListener<M, T>) => {
    messageListenersRef.current = messageListenersRef.current.filter(e => e !== listener)
  }, [])

  const subscribeMessage = useCallback(
    (listener: MessageListener<M, T>) => {
      messageListenersRef.current.push(listener)

      return () => unsubscribeMessage(listener)
    },
    [unsubscribeMessage],
  )

  const on = useCallback(
    <TT extends T>(type: TT, listener: MessageListener<M, TT>) => {
      return subscribeMessage(m => {
        if ('type' in m && m.type === type) {
          listener(m as M[TT])
        }
      })
    },
    [subscribeMessage],
  )

  return {
    isOpen,
    connect,
    disconnect,
    sendMessage,
    isConnectProcessing,
    subscribeMessage,
    unsubscribeMessage,
    on,
  }
}

export default useWebsocket
